import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, MessageCircleHeart, Compass, Target, Info, BookOpen } from 'lucide-react';
import PIELayout from './PIELayout';
import PIEGlossaryModal from './PIEGlossaryModal';
import { PIEPageSkeleton } from './PIESkeleton';
import FirstTimeTip, {
  getInitialTourStage,
  dismissAllTours,
  dismissTour,
  nextTourStage,
} from './PIEFirstTimeTip';
import { pieApi, pieCoachPreseed } from './pieApi';
import PIEPulseWidget from './PIEPulseWidget';
import PIEStreakPill from './PIEStreakPill';
import PIELearnerTrendCard from './PIELearnerTrendCard';
import PIEMotivationSurveyWidget from './PIEMotivationSurveyWidget';
// Dashboard subcomponents — extracted so this orchestrator stays
// readable. See dashboard/README via JSDoc on each file.
import { ThisWeekGroup, TriadStat, DetailsDisclosure, ErrorView } from './dashboard/PIEZoneShell';
import WhatsChangedStrip from './dashboard/PIEWhatsChangedStrip';
import RevivalNudge from './dashboard/PIERevivalNudge';
import CoachWhisper from './dashboard/PIECoachWhisper';
import DigestCTA from './dashboard/PIEDigestCTA';
import WeeklyPlanCard from './dashboard/PIEWeeklyPlanCard';
import PIEPastReflectionsCard from './dashboard/PIEPastReflectionsCard';
import PIEMonthInReviewCard from './dashboard/PIEMonthInReviewCard';
import PIEPullToRefresh from './dashboard/PIEPullToRefresh';
import { SkillRow, SkillDetailModal } from './dashboard/PIESkillProfile';
import KpiRow from './dashboard/PIEKpiRow';
import { pct, pct100 } from './format';
import { Perf, usePIEDisplayMode } from './PIEDisplayMode';
import './pie.css';
import logger from '../../lib/logger';
import usePullToRefresh from '../../hooks/usePullToRefresh';

/**
 * PIEDashboardPage — "My Current Effectiveness".
 * Orchestrates the 4-zone Today layout. All visual primitives live
 * in ./dashboard/. This file owns: data loading, tour state, persona
 * snapshot, weekly plan handlers, and zone composition.
 */
export default function PIEDashboardPage() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [togglingDay, setTogglingDay] = useState(null);
  const [streak, setStreak] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(null); // term key or null
  const [skillInfo, setSkillInfo] = useState(null); // skill object or null
  const [tourStage, setTourStage] = useState(() => getInitialTourStage());
  // Bumped on every pull-to-refresh trigger so child cards that
  // self-load on mount (PastReflections, MonthInReview) remount and
  // re-fetch via React's `key` propagation.
  const [refreshKey, setRefreshKey] = useState(0);

  const advanceTour = (key) => {
    dismissTour(key);
    setTourStage(nextTourStage(key));
  };
  const skipAllTour = () => {
    dismissAllTours();
    setTourStage(null);
  };

  /**
   * loadDashboard — fans out the three dashboard fetches in parallel.
   * Extracted from the mount effect so pull-to-refresh can re-call it
   * without duplicating the request graph. Returns a promise that
   * resolves once every leg has settled (success OR caught error) so
   * the pull spinner only stops when the whole view is up to date.
   */
  const loadDashboard = useCallback(async () => {
    const snapshotP = pieApi
      .snapshot()
      .then((s) => { setSnapshot(s); setErr(null); })
      .catch((e) => {
        logger.error('snapshot failed', e);
        setErr(e?.response?.data?.detail || 'Could not load snapshot');
      });
    const planP = pieApi
      .activeWeeklyPlan()
      .then((d) => setWeeklyPlan(d.plan || null))
      .catch((e) => logger.warn('weekly plan load failed', e));
    const streakP = pieApi
      .weeklyPlanStreak()
      .then(setStreak)
      .catch((e) => logger.warn('streak load failed', e));
    await Promise.allSettled([snapshotP, planP, streakP]);
  }, []);

  useEffect(() => {
    loadDashboard().finally(() => setLoading(false));
  }, [loadDashboard]);

  // Pull-to-refresh — touch only, no-op on desktop. Bumps refreshKey
  // so the self-loading reflection cards remount, AND re-fans the
  // primary dashboard fetch graph. Both finish before the spinner
  // clears (await chain). useCallback so the hook doesn't re-attach
  // its 4 window touch listeners on every parent render.
  const onPullRefresh = useCallback(async () => {
    setRefreshKey((k) => k + 1);
    await loadDashboard();
  }, [loadDashboard]);
  const { pullDistance, refreshing } = usePullToRefresh({ onRefresh: onPullRefresh });

  const cycleDayStatus = (current) => {
    // pending → done → skipped → pending
    if (current === 'pending') return 'done';
    if (current === 'done') return 'skipped';
    return 'pending';
  };

  const onToggleDay = async (planId, dayNum, currentStatus) => {
    if (togglingDay !== null) return;
    setTogglingDay(dayNum);
    try {
      const next = cycleDayStatus(currentStatus);
      const updated = await pieApi.toggleWeeklyPlanDay(planId, dayNum, next);
      setWeeklyPlan(updated);
    } catch (e) {
      logger.error('toggle day failed', e);
    } finally {
      setTogglingDay(null);
    }
  };

  const onArchivePlan = async (planId) => {
    try {
      await pieApi.archiveWeeklyPlan(planId);
      setWeeklyPlan(null);
    } catch (e) {
      logger.error('archive plan failed', e);
    }
  };

  const onRetrospectPlan = async (planId, payload) => {
    const updated = await pieApi.retrospectWeeklyPlan(planId, payload);
    // Keep the completed plan in state so the card renders the
    // celebratory summary. Auto-clear after a short pause so the
    // dashboard returns to its normal layout next render.
    setWeeklyPlan(updated);
    setTimeout(() => setWeeklyPlan(null), 6000);
    return updated;
  };

  /** Streak revival CTA — when the user has lapsed, offer a
   *  one-click handoff into the Coach with an empathetic restart
   *  prompt. Turns the streak from a guilt mechanic into a
   *  re-engagement lever. */
  const onRevive = () => {
    const seed =
      "I dropped off for a couple of weeks — let's restart with " +
      "something small that fits where I am right now. " +
      "Don't make me commit to a big plan; help me pick one tiny thing for this week.";
    pieCoachPreseed.set(seed);
    navigate('/mirror/coach');
  };

  if (loading) return <PIELayout><PIEPageSkeleton cards={3} /></PIELayout>;
  if (err) return <PIELayout><ErrorView err={err} /></PIELayout>;
  if (!snapshot) return null;

  const { user, capability, motivation, personal_effectiveness, personal_lei } = snapshot;
  const c = capability.score;
  const m = motivation.score;
  const pe = personal_effectiveness.score;
  const weakest = c <= m ? 'Capability' : 'Motivation';
  const firstName = user.name?.split(' ')[0] || 'there';
  const peGapPp = Math.max(0, Math.round((1 - pe) * 100));

  return (
    <PIELayout>
      <PIEPullToRefresh pullDistance={pullDistance} refreshing={refreshing} threshold={70} />
      <div className="pie-fade-in" data-testid="pie-dashboard">
        {/* Slim one-line "what changed since last visit" strip — user
            chose to keep it at the top, restyled lean. */}
        <WhatsChangedStrip compact />

        {/* ───────────────── ZONE 1 · Where you stand ───────────────── */}
        <section
          className="pie-zone-primary"
          data-testid="pie-zone-primary"
          style={{ marginBottom: 28 }}
        >
          {/* Compact one-line hero — no 3-line giant headline */}
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <span className="pie-eyebrow" style={{ display: 'inline-flex' }}>
              <Compass size={12} /> Your today
            </span>
            <h1
              className="pie-display-lg"
              style={{ marginTop: 10, marginBottom: 6, lineHeight: 1.15 }}
            >
              Hi {firstName}.{' '}
              <span style={{ color: '#44b6d2' }}>Your biggest opportunity is</span>{' '}
              <span className="pie-accent-text">{weakest}</span>.
            </h1>
            <div
              style={{
                display: 'flex', gap: 10, alignItems: 'center',
                justifyContent: 'center', flexWrap: 'wrap', marginTop: 10,
              }}
            >
              <PIEStreakPill />
              <button
                type="button"
                className="pie-glossary-link"
                onClick={() => setGlossaryOpen('overview')}
                data-testid="pie-glossary-trigger"
                style={{ fontSize: 12 }}
              >
                <BookOpen size={11} /> What do these terms mean?
              </button>
            </div>
          </div>

          {/* Unified PE card — big number + gap callout + C/M/LEI triad
              all in one surface so the user reads vertical context
              without re-orienting between cards. */}
          <section
            className="pie-card pie-card-elevated"
            style={{
              padding: '28px 28px 20px',
              position: 'relative',
              zIndex: tourStage === 'pe' ? 30 : 'auto',
            }}
            data-testid="pie-pe-card"
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr minmax(220px, 320px)',
                gap: 24, alignItems: 'center',
              }}
              className="pie-pe-hero-grid"
            >
              <div>
                <span className="pie-eyebrow" style={{ marginBottom: 10 }}>
                  <Target size={12} /> Personal Effectiveness
                  <button
                    type="button"
                    className="pie-eyebrow-info"
                    onClick={() => setGlossaryOpen('pe')}
                    data-testid="pie-pe-info"
                    aria-label="What is Personal Effectiveness?"
                    title="What is Personal Effectiveness?"
                  >
                    <Info size={11} />
                  </button>
                </span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 10 }}>
                  <span className="pie-display-xl" data-testid="pie-pe-score"><Perf value={pe} kind="pct01" /></span>
                  <span className="pie-chip pie-chip-accent">
                    {personal_effectiveness.interpretation}
                  </span>
                </div>
                {peGapPp > 0 && (
                  <p
                    style={{
                      marginTop: 14, fontSize: 13, color: 'var(--pie-text-secondary)',
                      lineHeight: 1.55, maxWidth: 460,
                    }}
                    data-testid="pie-pe-gap-callout"
                  >
                    <PEGapHeadline peGapPp={peGapPp} weakest={weakest} />
                  </p>
                )}
                <p
                  style={{
                    marginTop: 10, fontSize: 12, color: 'var(--pie-text-muted)',
                    lineHeight: 1.55, maxWidth: 460,
                  }}
                >
                  PE = C × M. Multiplicative, not additive — a strong score in one
                  factor can&apos;t paper over a weak one.
                </p>
              </div>

              {/* C / M / LEI micro-triad — compact, same surface */}
              <div
                className="pie-pe-triad"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 10,
                  borderLeft: '1px solid var(--pie-border)', paddingLeft: 22,
                }}
              >
                <TriadStat
                  label="Capability"
                  value={c}
                  sub={`Across ${capability.breakdown?.length || 0} skills`}
                  testid="pie-c-card"
                  onInfo={() => setGlossaryOpen('capability')}
                />
                <TriadStat
                  label="Motivation"
                  value={m}
                  sub={<>Manager support: <Perf value={motivation.manager_support || 0} kind="pct01" /></>}
                  testid="pie-m-card"
                  onInfo={() => setGlossaryOpen('motivation')}
                  highlight={weakest === 'Motivation'}
                />
                <TriadStat
                  label="Personal LEI"
                  value={personal_lei.score / 100}
                  sub={<><Perf value={personal_lei.score} kind="pct100" /> learning quality</>}
                  testid="pie-lei-card"
                  onInfo={() => setGlossaryOpen('lei')}
                />
              </div>
            </div>
            {tourStage === 'pe' && (
              <FirstTimeTip
                tipKey="pe"
                isLast={false}
                onAdvance={() => advanceTour('pe')}
                onSkipAll={skipAllTour}
                position="below"
              />
            )}
          </section>
        </section>

        {/* ───────────────── ZONE 2 · This week's focus ───────────────── */}
        <ThisWeekGroup
          hasRevival={!!streak?.lapsed && !weeklyPlan}
        >
          {streak?.lapsed && !weeklyPlan && (
            <RevivalNudge streak={streak} onRevive={onRevive} />
          )}
          <PIEMotivationSurveyWidget />
          <CoachWhisper />
          <PIEPulseWidget />
          {weeklyPlan && (
            <WeeklyPlanCard
              plan={weeklyPlan}
              togglingDay={togglingDay}
              onToggleDay={onToggleDay}
              onArchive={onArchivePlan}
              onRetrospect={onRetrospectPlan}
            />
          )}
          <PIEMonthInReviewCard key={`mir-${refreshKey}`} />
          <PIEPastReflectionsCard key={`past-${refreshKey}`} />
          <DigestCTA />
        </ThisWeekGroup>

        {/* ───────────────── ZONE 3 · Context & detail ───────────────── */}
        <section data-testid="pie-zone-context" style={{ marginBottom: 28 }}>
          <PIELearnerTrendCard />

          <DetailsDisclosure
            label="Show my skill profile & $ contribution"
            testid="pie-details-disclosure"
          >
            <section className="pie-card" style={{ marginBottom: 16 }} data-testid="pie-skills-card">
              <div className="pie-score-eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Your skill profile</span>
                <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--pie-text-muted)' }}>
                  vs the level your role requires
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {capability.breakdown?.map((s) => (
                  <SkillRow
                    key={s.skill_name}
                    skill={s}
                    onInfo={() => setSkillInfo(s)}
                  />
                ))}
              </div>
            </section>

            <section className="pie-card" data-testid="pie-kpi-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <div className="pie-score-eyebrow" style={{ marginBottom: 0 }}>The business outcomes you move</div>
                <span
                  className="pie-inline-tip pie-kpi-totals-tip"
                  data-tooltip="Annualised dollar values. Estimated per-person economic uplift attributable to learning at your role's KPI baselines, scaled by your current Personal Effectiveness."
                  tabIndex={0}
                >
                  Annual $ per person · scaled by your PE
                </span>
              </div>
              <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', marginBottom: 18, lineHeight: 1.6 }}>
                Your role contributes to these business outcomes. The number is the
                annual dollar value <strong>you&apos;re currently delivering</strong> per person, with the
                headroom you&apos;d unlock by closing the gap to 100% Personal Effectiveness.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(snapshot.kpi_contributions || []).map((row) => (
                  <KpiRow key={row.kpi_id} row={row} />
                ))}
                {(!snapshot.kpi_contributions || snapshot.kpi_contributions.length === 0) && (
                  <div style={{ fontSize: 13, color: 'var(--pie-text-muted)', fontStyle: 'italic' }}>
                    No KPIs assigned to your role yet.
                  </div>
                )}
              </div>
            </section>
          </DetailsDisclosure>
        </section>

        {/* ───────────────── ZONE 4 · Next steps ───────────────── */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/mirror/improve" className="pie-btn pie-btn-primary" data-testid="pie-cta-improve">
            <Target size={14} /> What should I improve? <ArrowRight size={14} />
          </Link>
          <Link to="/mirror/simulate" className="pie-btn pie-btn-ghost" data-testid="pie-cta-simulate">
            <Sparkles size={14} /> Try a what-if
          </Link>
          <Link to="/mirror/coach" className="pie-btn pie-btn-ghost" data-testid="pie-cta-coach">
            <MessageCircleHeart size={14} /> Talk to my coach
          </Link>
        </div>
      </div>
      <PIEGlossaryModal
        open={!!glossaryOpen}
        focus={glossaryOpen}
        onClose={() => setGlossaryOpen(null)}
      />
      {skillInfo && (
        <SkillDetailModal skill={skillInfo} onClose={() => setSkillInfo(null)} />
      )}
    </PIELayout>
  );
}

/**
 * PEGapHeadline — surfaces the "X pp of headroom" copy. In verbiage
 * mode we replace the precise gap value with a qualitative descriptor
 * so learners aren't anchored to a number their org has chosen to hide.
 */
function PEGapHeadline({ peGapPp, weakest }) {
  const { mode } = usePIEDisplayMode();
  if (mode === 'verbiage') {
    return (
      <>
        <strong style={{ color: 'var(--pie-text)' }}>Room to grow</strong> —
        that&apos;s the gap between where you are and full drive. Closing
        even part of it usually comes from the <strong>{weakest}</strong> side.
      </>
    );
  }
  return (
    <>
      <strong style={{ color: 'var(--pie-text)' }}>{peGapPp}pp of headroom</strong> —
      that&apos;s the gap between where you are and full drive. Closing even
      half of it usually comes from the <strong>{weakest}</strong> side.
    </>
  );
}
